import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Box, TextField, Typography } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { passwordStrength } from '€lib/strings/passwordStrength';
import { PasswordStrengthErrors } from '€lib/strings/types';
import { requestResetPassword } from '€slices/password-reset';
import {
    getResetPasswordDidFailed,
    getResetPasswordSuccess,
    getResettingPassword,
} from '€slices/password-reset/password-reset-selectors';
import PasswordStrength from '€components/PasswordStrength';
import PrimaryButton from '€components/PrimaryButton';
import ContentLayout from '€layouts/ContentLayout';
import CenterSmUp from '€components/CenterSmUp';
import { Routes } from '€lib/routing';

type Props = {
    token?: string;
};

const PasswordResetForm: React.FC<Props> = ({ token }) => {
    const { register, handleSubmit, setError, errors, getValues } = useForm({
        mode: 'onChange',
    });
    const dispatch = useDispatch();

    const resettingPassword = useSelector(getResettingPassword);
    const resetPasswordSuccess = useSelector(getResetPasswordSuccess);
    const resetPasswordFailed = useSelector(getResetPasswordDidFailed);

    const [passwordErrors, setPasswordErrors] = useState<PasswordStrengthErrors>({});
    const [passwordsValid, setPasswordsValid] = useState<boolean>(false);

    useEffect(() => {
        const passwordStr = passwordStrength('', '');
        setPasswordErrors(passwordStr[1]);
        setPasswordsValid(passwordStr[0]);
    }, []);

    const onSubmit = useCallback(
        async ({ currentPassword, newPassword }) => {
            try {
                await dispatch(requestResetPassword({ currentPassword, newPassword }));
            } catch (e) {
                setError('password', { type: 'manual', message: e.message });
            }
        },
        [dispatch, setError],
    );

    const handleValidate = useCallback(
        value => {
            const { newPassword, confirmNewPassword } = getValues();
            const strength = passwordStrength(newPassword, confirmNewPassword);
            setPasswordErrors(strength[1]);
            setPasswordsValid(strength[0]);

            return true;
        },
        [getValues],
    );

    return (
        <Box width={{ lg: '40rem' }}>
            <ContentLayout.Section>
                <CenterSmUp>
                    <Typography>What would you like your new password to be?</Typography>
                </CenterSmUp>
            </ContentLayout.Section>
            <form onSubmit={handleSubmit(onSubmit)}>
                <ContentLayout.Form>
                    <ContentLayout.FormField>
                        <TextField
                            required
                            inputRef={register({
                                required: true,
                            })}
                            type="password"
                            name="currentPassword"
                            label="Current Password"
                            autoFocus
                        />
                    </ContentLayout.FormField>
                    <ContentLayout.FormField>
                        <TextField
                            required
                            inputRef={register({
                                required: true,
                                validate: {
                                    isOk: handleValidate,
                                },
                            })}
                            type="password"
                            name="newPassword"
                            label="New Password"
                        />
                    </ContentLayout.FormField>
                    <ContentLayout.FormField>
                        <TextField
                            required
                            inputRef={register({
                                required: true,
                                validate: {
                                    isOk: handleValidate,
                                },
                            })}
                            type="password"
                            name="confirmNewPassword"
                            label="Confirm New Password"
                        />
                    </ContentLayout.FormField>
                    <PasswordStrength errors={passwordErrors} />
                    <ContentLayout.Action>
                        <Box mt="3.125rem" display="flex" justifyContent="center">
                            <PrimaryButton
                                size={['full', 'large']}
                                type="submit"
                                disabled={!passwordsValid || resettingPassword}
                            >
                                Change Password
                            </PrimaryButton>
                        </Box>
                    </ContentLayout.Action>
                    {resetPasswordFailed && (
                        <CenterSmUp>
                            <Box p={1}>
                                <Typography color="error">
                                    {errors.password?.message ||
                                        "Your current password is incorrect, it's required to change the Password."}
                                </Typography>
                            </Box>
                        </CenterSmUp>
                    )}
                </ContentLayout.Form>
            </form>
            {resetPasswordSuccess && <Redirect to={Routes.Login} />}
        </Box>
    );
};

export default React.memo(PasswordResetForm);
